import {match, Pattern} from 'ts-pattern';

import {always, isNil} from 'ramda';

import {GridTemplate} from '../../types/GridTemplate';
import {Integer} from '../../types/Integer';
import {getSize} from '../../utils/getSize';

interface GridTemplateColumnsOptions {
  columns?: number | GridTemplate;
  minColumnWidth?: Integer;
  maxColumns?: Integer;
  fill?: boolean;
  horizontalGap: Integer;
}

/** `getSize` yields a bare `0`, which is not a valid length inside `calc()`, `min()` and `max()`. */
function getLength(size: Integer) {
  return size === 0 ? '0rem' : getSize(size);
}

function getColumnWidth(
  minColumnWidth: Integer,
  maxColumns: Integer | undefined,
  horizontalGap: Integer
) {
  if (isNil(maxColumns)) {
    return getLength(minColumnWidth);
  }

  // A column never gets narrower than the share it would have in a row of `maxColumns` columns.
  const gapsWidth = `${maxColumns - 1} * ${getLength(horizontalGap)}`;

  return `max(${getLength(minColumnWidth)}, calc((100% - ${gapsWidth}) / ${maxColumns}))`;
}

export function getGridTemplateColumns(options: GridTemplateColumnsOptions) {
  return match(options)
    .with({minColumnWidth: Pattern.number}, (value) => {
      const repeatMode = value.fill ? 'auto-fill' : 'auto-fit';
      const width = getColumnWidth(value.minColumnWidth, value.maxColumns, value.horizontalGap);

      return `repeat(${repeatMode}, minmax(min(100%, ${width}), 1fr))`;
    })
    .with({columns: Pattern.number}, (value) => `repeat(${value.columns}, minmax(0, 1fr))`)
    .with({columns: {template: Pattern.string}}, (value) => value.columns.template)
    .otherwise(always('none'));
}
